import { useRouter, type AnyRouter } from '@tanstack/react-router';
import { z } from 'zod';
import type { AppRouteMeta } from '@/routing/route-meta';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import { useAccessContext } from '@/routing/useAccessContext';
import { canAccessAppRouteMeta } from '@/routing/access-meta';
import { validateRoute } from './routeValidation';

/** How long back/forward waits for the router to settle before reporting. */
const HISTORY_SETTLE_MS = 2_000;

const searchValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

const openPageParams = z.object({
  path: z
    .string()
    .describe(
      'In-app path, optionally with a query string: a path returned by find_pages (e.g. "/sales-orders-v3") or a concrete record URL (e.g. "/sales-orders-v3/123?tab=lines").',
    ),
});

const setUrlParamsParams = z.object({
  params: z
    .record(z.string(), searchValueSchema)
    .describe(
      'Query params to set on the current page, merged into the existing ones. A null value removes that param.',
    ),
  replace: z
    .boolean()
    .optional()
    .describe(
      'True to replace the current history entry instead of adding one. Defaults to false.',
    ),
});

const noParams = z.object({});

/** Where the user is now, read after the router settles. */
function describeLocation(router: AnyRouter) {
  const { pathname, search } = router.state.location;
  return { pathname, search };
}

/** Resolves once the next navigation resolves, or after `timeoutMs`. */
function waitForResolved(router: AnyRouter, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    const unsubscribe = router.subscribe('onResolved', done);
  });
}

function mergeSearch(
  prev: Record<string, unknown>,
  patch: Record<string, z.infer<typeof searchValueSchema>>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...prev };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) delete next[key];
    else next[key] = value;
  }
  return next;
}

export interface NavigationToolsOptions {
  /**
   * Called just before the agent leaves the current page. The full-page chat
   * uses it to carry the conversation into the docked panel.
   */
  onBeforeLeave?: () => void;
}

/**
 * Browser-executed navigation for Nami: open a page, go back/forward, change
 * the current page's query params, and read where the user is. Destinations
 * are validated against the routes this user can access; each tool waits for
 * the router to settle and reports the resulting location, so the model sees
 * redirects (e.g. a `beforeLoad` guard bouncing the user).
 */
export function useNavigationTools(options: NavigationToolsOptions = {}) {
  const router = useRouter();
  const accessCtx = useAccessContext();
  const { onBeforeLeave } = options;
  const canAccess = (meta: AppRouteMeta) =>
    canAccessAppRouteMeta(meta, accessCtx);

  const validateRouteTool = useFrontendTool({
    name: 'validate_route',
    description:
      "Check an in-app URL the way the router will, without navigating: that the page exists and this user can reach it, that its path params are valid, and that its query params are ones the page accepts. Returns ok with the page's accepted query params, or the reason it is invalid. open_page and set_url_params run the same check, so use this when unsure of a path or param.",
    parameters: openPageParams,
    awaitable: true,
    readOnlyHint: true,
    execute: ({ path }) => validateRoute(router, path, canAccess),
  });

  const openPage = useFrontendTool({
    name: 'open_page',
    description:
      'Navigate the user to another page in the app right now. Use when the user asks to go to, open, or take them to a page or record. Get static paths from find_pages; record URLs may be passed directly. Returns the location the user landed on.',
    parameters: openPageParams,
    awaitable: true,
    readOnlyHint: true,
    execute: async ({ path }) => {
      const target = validateRoute(router, path, canAccess);
      if (!target.ok) return target;
      onBeforeLeave?.();
      await router.navigate({ href: target.href });
      return describeLocation(router);
    },
  });

  const goBack = useFrontendTool({
    name: 'go_back',
    description:
      "Go back one step in the browser history, like the browser's back button. Returns the location the user landed on.",
    parameters: noParams,
    awaitable: true,
    readOnlyHint: true,
    execute: async () => {
      if (!router.history.canGoBack()) {
        return { ok: false, error: 'There is no earlier page to go back to.' };
      }
      onBeforeLeave?.();
      const settled = waitForResolved(router, HISTORY_SETTLE_MS);
      router.history.back();
      await settled;
      return describeLocation(router);
    },
  });

  const goForward = useFrontendTool({
    name: 'go_forward',
    description:
      "Go forward one step in the browser history, like the browser's forward button. Returns the location the user landed on.",
    parameters: noParams,
    awaitable: true,
    readOnlyHint: true,
    execute: async () => {
      const before = router.state.location.href;
      onBeforeLeave?.();
      const settled = waitForResolved(router, HISTORY_SETTLE_MS);
      router.history.forward();
      await settled;
      if (router.state.location.href === before) {
        return { ok: false, error: 'There is no later page to go forward to.' };
      }
      return describeLocation(router);
    },
  });

  const setUrlParams = useFrontendTool({
    name: 'set_url_params',
    description:
      "Set or remove query params on the current page's URL (tabs, filters, selected record, paging). Only params the page accepts are allowed: an unknown param is rejected with the list the page takes.",
    parameters: setUrlParamsParams,
    awaitable: true,
    readOnlyHint: true,
    execute: async ({ params, replace }) => {
      // Built as an href with the router's own serializer: the typed
      // `search` updater can't be expressed for an arbitrary current route.
      const { pathname, search, hash } = router.state.location;
      const query = router.options.stringifySearch(mergeSearch(search, params));
      const target = validateRoute(router, `${pathname}${query}`, canAccess);
      if (!target.ok) return target;
      await router.navigate({
        href: `${target.href}${hash ? `#${hash}` : ''}`,
        replace: replace ?? false,
      });
      return describeLocation(router);
    },
  });

  const getCurrentLocation = useFrontendTool({
    name: 'get_current_location',
    description:
      "Read the user's current page path and query params, and whether there is history to go back to.",
    parameters: noParams,
    awaitable: true,
    readOnlyHint: true,
    execute: () => ({
      ...describeLocation(router),
      canGoBack: router.history.canGoBack(),
    }),
  });

  return [
    validateRouteTool,
    openPage,
    goBack,
    goForward,
    setUrlParams,
    getCurrentLocation,
  ] as const;
}
